/**
 * OnboardingWizard · 4-slide modal post-login para primeros usuarios.
 *
 * Sprint Campus Wave 2 · 2026-05-27 · Carlos (frontend) + Andrés (backend)
 *
 * Behavior:
 * - Server-driven · trigger basado en `profile.onboarding_completed_at` y
 *   `profile.onboarding_dismissed_at` (ambos NULL → mostrar).
 * - Skip/Esc/click backdrop → POST /api/me/onboarding/dismiss · cierra
 *   sin marcar completado (permite re-trigger desde /perfil).
 * - Finalizar en última slide → POST /api/me/onboarding/complete.
 * - Keyboard nav: ← → para slides · Esc para cerrar · focus trap dentro del modal.
 * - Mobile-responsive · brand gradient bg · framer-motion slide transitions.
 * - Re-trigger desde fuera via eventos `window` `onboarding:open` /
 *   `onboarding:close` (usado por Perfil "Mostrar tutorial").
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../lib/auth-context';
import {
  completeOnboarding,
  dismissOnboarding,
  fetchProfile,
  isApiError,
} from '../lib/api';

interface Slide {
  title: string;
  body: string;
  icon: string;
  highlight?: string;
}

const SLIDES: Slide[] = [
  {
    title: 'Bienvenido a Robotea Campus',
    body: 'Vas a aprender a operar con robots de trading automatizado, paso a paso. Tu acceso ya está activo y todo tu progreso se guarda solo. Esta visita guiada te enseña dónde está cada cosa en menos de un minuto.',
    icon: '🚀',
    highlight: 'Tu curso ya está listo · empezamos',
  },
  {
    title: 'Completa tu perfil',
    body: 'Dedica un momento a completar tu perfil con tu nombre y tus datos de contacto. Así el equipo puede darte soporte y avisarte de lo importante. Lo encuentras siempre en "Perfil".',
    icon: '👤',
    highlight: 'Perfil → completa tus datos de contacto',
  },
  {
    title: 'Tu progreso, en vivo',
    body: 'Cada módulo tiene un vídeo y un checklist: complétalos para desbloquear el siguiente. Tu racha, tu nivel y tu rango suben cada vez que avanzas. En "Inicio" siempre verás tu próximo paso recomendado.',
    icon: '✅',
    highlight: 'Racha · Nivel · Rango · Próximo paso',
  },
  {
    title: 'Únete a la comunidad',
    body: 'Tienes un Discord privado para resolver dudas con otros alumnos y con el equipo Robotea. Los anuncios importantes los publicamos allí primero. Vincúlalo desde tu Perfil.',
    icon: '💬',
    highlight: 'Discord privado de alumnos Robotea',
  },
  {
    title: 'Tus siguientes pasos',
    body: 'Ya lo tienes todo para empezar:\n\n1 · Completa tu perfil y vincula tu Discord.\n2 · Empieza tu curso desde "Inicio" (tu próximo paso ya está marcado).\n3 · Cuando el curso te lo indique, activa tu robot en "Mis robots".\n\n¿Dudas en cualquier momento? Abre la Wiki o pregunta al Tutor IA (botón ✨ abajo a la derecha).',
    icon: '🧭',
    highlight: 'Inicio · Perfil · Mis robots · Wiki · Tutor IA',
  },
];

const TOTAL_SLIDES = SLIDES.length;
const OPEN_EVENT = 'onboarding:open';
const CLOSE_EVENT = 'onboarding:close';

export default function OnboardingWizard(): JSX.Element | null {
  const { state } = useAuth();
  const auth = state.status === 'authenticated' ? state.identity : null;

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // Ref para no re-fetchear el profile en cada cambio de state (evita
  // race condition donde cleanup invalidaría el promise antes de resolver).
  const triggeredRef = useRef(false);
  const lastAuthEmailRef = useRef<string | null>(null);

  // 1) Auto-trigger on auth · sólo si ambos flags son NULL en el profile.
  //    Se ejecuta UNA vez por sesión auth (cambio de email resetea).
  useEffect(() => {
    // Reset si el usuario cambió (signOut/signIn diferente)
    if (lastAuthEmailRef.current && lastAuthEmailRef.current !== (auth?.email ?? null)) {
      triggeredRef.current = false;
    }
    lastAuthEmailRef.current = auth?.email ?? null;

    if (!auth) {
      setOpen(false);
      return;
    }
    if (triggeredRef.current) return;
    triggeredRef.current = true;

    let active = true;
    fetchProfile(auth)
      .then((profile) => {
        if (!active) return;
        const shouldShow =
          profile.onboarding_completed_at === null &&
          profile.onboarding_dismissed_at === null;
        if (shouldShow) {
          setStep(0);
          setOpen(true);
        }
      })
      .catch((err: unknown) => {
        if (!active) return;
        // Best-effort · si /api/me/profile falla (network, 404 nuevo user
        // sin row en BD aún, etc.) no abrimos el wizard pero tampoco bloqueamos
        // el Dashboard. El usuario lo puede abrir desde Perfil.
        // eslint-disable-next-line no-console
        console.warn(
          '[OnboardingWizard] fetchProfile failed · wizard skipped:',
          isApiError(err) ? err.detail : err,
        );
      });
    return () => {
      active = false;
    };
  }, [auth]);

  // 2) Listener para re-trigger externo (Perfil "Mostrar tutorial")
  useEffect(() => {
    const openHandler = (): void => {
      setOpen(true);
      setStep(0);
    };
    const closeHandler = (): void => {
      setOpen(false);
    };
    window.addEventListener(OPEN_EVENT, openHandler);
    window.addEventListener(CLOSE_EVENT, closeHandler);
    return () => {
      window.removeEventListener(OPEN_EVENT, openHandler);
      window.removeEventListener(CLOSE_EVENT, closeHandler);
    };
  }, []);

  const handleDismiss = useCallback(async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setOpen(false);
    try {
      if (auth) await dismissOnboarding(auth);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.warn(
        '[OnboardingWizard] dismiss failed:',
        isApiError(err) ? err.detail : err,
      );
    } finally {
      setBusy(false);
    }
  }, [auth, busy]);

  const handleComplete = useCallback(async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setOpen(false);
    try {
      if (auth) await completeOnboarding(auth);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.warn(
        '[OnboardingWizard] complete failed:',
        isApiError(err) ? err.detail : err,
      );
    } finally {
      setBusy(false);
    }
  }, [auth, busy]);

  const next = useCallback((): void => {
    setStep((s) => Math.min(s + 1, TOTAL_SLIDES - 1));
  }, []);

  const prev = useCallback((): void => {
    setStep((s) => Math.max(s - 1, 0));
  }, []);

  // 3) Keyboard navigation (Esc · ArrowLeft · ArrowRight)
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        void handleDismiss();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (step < TOTAL_SLIDES - 1) {
          next();
        } else {
          void handleComplete();
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, handleDismiss, handleComplete, next, prev]);

  // 4) Focus al abrir (focus trap simple · primer botón visible)
  useEffect(() => {
    if (!open) return;
    const node = dialogRef.current;
    if (!node) return;
    // Pequeño delay para esperar el mount del AnimatePresence.
    const t = window.setTimeout(() => {
      const btn = node.querySelector<HTMLButtonElement>(
        'button[data-onboarding-primary="true"]',
      );
      btn?.focus();
    }, 80);
    return () => window.clearTimeout(t);
  }, [open, step]);

  if (!open) return null;

  const slide = SLIDES[step];
  const isLast = step === TOTAL_SLIDES - 1;

  return (
    <AnimatePresence>
      <motion.div
        key="onboarding-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            void handleDismiss();
          }
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        aria-describedby="onboarding-body"
      >
        <motion.div
          key={`onboarding-card-${step}`}
          ref={dialogRef}
          initial={{ opacity: 0, y: 10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className={
            // En CLARO la tarjeta del modal es superficie sólida (bg-surface); el degradado navy hex
            // solo aplica en OSCURO (dark:*). El backdrop oscuro (bg-black/80) es scrim, no se toca.
            'relative w-full max-w-lg overflow-hidden rounded-2xl border border-ink/10 bg-surface p-6 shadow-2xl dark:bg-gradient-to-br dark:from-[#15102a] dark:via-[#12122a] dark:to-[#0c0a18] sm:p-8'
          }
        >
          {/* Close X (mobile-friendly) */}
          <button
            type="button"
            onClick={() => void handleDismiss()}
            aria-label="Cerrar tutorial"
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-ink/40 transition hover:bg-ink/10 hover:text-ink"
            disabled={busy}
          >
            <span aria-hidden="true">×</span>
          </button>

          {/* Counter */}
          <p className="mb-2 text-[11px] uppercase tracking-[0.25em] text-brand-violet">
            Paso {step + 1} de {TOTAL_SLIDES}
          </p>

          {/* Slide content · AnimatePresence interno para slide-in/out */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <div className="mb-5 text-5xl" aria-hidden="true">
                {slide.icon}
              </div>
              <h2
                id="onboarding-title"
                className="mb-3 text-xl font-bold text-ink sm:text-2xl"
              >
                {step === 0 && auth?.name
                  ? `${slide.title}, ${auth.name}`
                  : slide.title}
              </h2>
              <p
                id="onboarding-body"
                className="mb-4 whitespace-pre-line text-sm leading-relaxed text-ink/70 sm:text-base"
              >
                {slide.body}
              </p>
              {slide.highlight && (
                <p className="mb-6 inline-flex items-center gap-2 rounded-lg border border-brand-violet/30 bg-brand-violet/10 px-3 py-2 text-xs text-ink sm:text-sm">
                  <span aria-hidden="true">✨</span>
                  {slide.highlight}
                </p>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Progress dots */}
          <div
            className="mb-6 flex justify-center gap-1.5"
            role="tablist"
            aria-label="Progreso del tutorial"
          >
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === step}
                aria-label={`Ir al paso ${i + 1}`}
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === step
                    ? 'w-8 bg-brand-violet'
                    : 'w-2 bg-ink/15 hover:bg-ink/30'
                }`}
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={() => void handleDismiss()}
              disabled={busy}
              className="rounded-lg px-3 py-2 text-sm text-ink/50 transition hover:text-ink/80 disabled:opacity-50 sm:flex-none"
            >
              Saltar
            </button>
            {step > 0 && (
              <button
                type="button"
                onClick={prev}
                disabled={busy}
                className="flex-1 rounded-lg border border-ink/10 px-4 py-2.5 text-sm text-ink/80 transition hover:bg-ink/5 disabled:opacity-50"
              >
                Anterior
              </button>
            )}
            {!isLast ? (
              <button
                type="button"
                data-onboarding-primary="true"
                onClick={next}
                disabled={busy}
                className="flex-1 rounded-lg bg-brand-violet px-4 py-2.5 text-sm font-semibold text-ink transition hover:bg-brand-violet/80 disabled:opacity-50"
              >
                Siguiente
              </button>
            ) : (
              <button
                type="button"
                data-onboarding-primary="true"
                onClick={() => void handleComplete()}
                disabled={busy}
                className="flex-1 rounded-lg bg-brand-violet px-4 py-2.5 text-sm font-semibold text-ink transition hover:bg-brand-violet/80 disabled:opacity-50"
              >
                {busy ? 'Guardando…' : 'Finalizar'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/** Helper exportado para que Perfil pueda re-abrir el wizard tras reset. */
export function openOnboardingWizard(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/** Helper exportado · uso externo opcional (programmatic close). */
export function closeOnboardingWizard(): void {
  window.dispatchEvent(new CustomEvent(CLOSE_EVENT));
}
